import axios from 'axios';
import config from 'config';
import store from 'store';
import { AppActions } from 'modules/app/appActions';
import { CognitoTokenManager } from 'app/modules/auth/utils/cognitoTokenManager';
import { getTokenExpirationTime } from 'app/modules/auth/utils/jwtUtils';
import { RefreshTokenResponse } from 'app/modules/auth/api/cognitoApi';
import Logger from 'utils/logger';
import i18n from 'app/utils/i18n';

const refreshClient = axios.create({
  baseURL: config.PUBLIC_API_SERVICE_ENDPOINT,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    accept: 'application/json',
  },
});

let refreshPromise: Promise<RefreshTokenResponse> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;

const REFRESH_BUFFER_SECONDS = 300;

function clearProactiveTimer(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function isTokenNearExpiry(accessToken: string): boolean {
  const expirationTime = getTokenExpirationTime(accessToken);

  if (!expirationTime) {
    return true;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  return expirationTime - nowSeconds <= REFRESH_BUFFER_SECONDS;
}

function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible') {
    return;
  }

  const tokens = CognitoTokenManager.getTokens();

  if (!tokens?.accessToken) {
    return;
  }

  if (isTokenNearExpiry(tokens.accessToken)) {
    TokenRefreshService.refresh().catch(() => {});
  } else {
    scheduleProactiveRefresh(tokens.accessToken);
  }
}

function attachVisibilityListener(): void {
  if (visibilityListenerAttached) {
    return;
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = true;
}

function detachVisibilityListener(): void {
  if (!visibilityListenerAttached) {
    return;
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = false;
}

function scheduleProactiveRefresh(accessToken: string): void {
  clearProactiveTimer();

  const expirationTime = getTokenExpirationTime(accessToken);

  if (!expirationTime) {
    return;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const secondsUntilRefresh =
    expirationTime - nowSeconds - REFRESH_BUFFER_SECONDS;

  if (secondsUntilRefresh <= 0) {
    TokenRefreshService.refresh().catch(() => {});
    return;
  }

  proactiveRefreshTimer = setTimeout(() => {
    TokenRefreshService.refresh().catch(() => {});
  }, secondsUntilRefresh * 1000);
}

export function updateTokensEverywhere(response: RefreshTokenResponse): void {
  const currentTokens = CognitoTokenManager.getTokens();

  CognitoTokenManager.setTokens({
    idToken: response.id_token,
    accessToken: response.access_token,
    refreshToken: response.refresh_token || currentTokens?.refreshToken || '',
  });

  store.dispatch(AppActions.setAuthToken(response.access_token));

  scheduleProactiveRefresh(response.access_token);
}

export const TokenRefreshService = {
  /**
   * The in-flight refresh, or null when none is running. Lets a caller wait
   * for a refresh that is already underway *without* starting one — the
   * request interceptor uses it to avoid firing a request with a token that
   * is about to be replaced.
   */
  getPendingRefresh(): Promise<RefreshTokenResponse> | null {
    return refreshPromise;
  },

  /**
   * True only when the stored access token is decodable and already past its
   * `exp`. Distinct from the proactive {@link REFRESH_BUFFER_SECONDS} window:
   * this means "a request sent right now is certain to fail", not "renew
   * soon".
   *
   * A missing or undecodable token returns false: we can't establish that it
   * is dead, and answering true would force a refresh ahead of every single
   * request. Those cases go out as-is and fall through to normal 401 handling.
   */
  isAccessTokenExpired(): boolean {
    const accessToken = CognitoTokenManager.getTokens()?.accessToken;

    if (!accessToken) {
      return false;
    }

    const expirationTime = getTokenExpirationTime(accessToken);

    if (!expirationTime) {
      return false;
    }

    return expirationTime <= Math.floor(Date.now() / 1000);
  },

  refresh(): Promise<RefreshTokenResponse> {
    if (refreshPromise) {
      Logger.log('[Auth][Refresh] Refresh already in progress, deduplicating');
      return refreshPromise;
    }

    const tokens = CognitoTokenManager.getTokens();
    const refreshToken = tokens?.refreshToken;

    if (!refreshToken) {
      Logger.error(
        '[Auth][Refresh] No refresh token available, cannot refresh',
      );
      return Promise.reject(new Error(i18n.t('auth.noRefreshToken')));
    }

    Logger.log(
      `[Auth][Refresh] Starting token refresh (refreshToken: ${refreshToken.length} chars)`,
    );
    refreshPromise = refreshClient
      .post('/cognito/refresh', {
        refresh_token: refreshToken,
      })
      .then((response) => {
        const data = response.data?.data ?? response.data;
        Logger.log(
          `[Auth][Refresh] Refresh succeeded: access_token=${
            data.access_token ? `${data.access_token.length} chars` : 'MISSING'
          }, id_token=${
            data.id_token ? `${data.id_token.length} chars` : 'MISSING'
          }, refresh_token=${
            data.refresh_token
              ? `${data.refresh_token.length} chars`
              : 'MISSING'
          }`,
        );

        updateTokensEverywhere(data);

        return data;
      })
      .catch((error) => {
        Logger.error(
          `[Auth][Refresh] Token refresh FAILED: ${
            error instanceof Error
              ? error.message
              : i18n.t('somethingWentWrong')
          } | status=${
            (error as any)?.response?.status
          } | data=${JSON.stringify((error as any)?.response?.data)}`,
        );
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  },

  initializeProactiveRefresh(): void {
    const tokens = CognitoTokenManager.getTokens();

    if (tokens?.accessToken) {
      scheduleProactiveRefresh(tokens.accessToken);
    }

    attachVisibilityListener();
  },

  cleanup(): void {
    clearProactiveTimer();
    detachVisibilityListener();
    refreshPromise = null;
  },
};

export default TokenRefreshService;
